// Downloading a programme as a PDF — the logic, kept out of the button so it
// can be tested without rendering.
//
// # TWO WAYS TO SAVE A FILE, AND WHY
//
// On the web, a blob URL on an <a download> saves the file. In the iOS shell
// that does nothing at all: WKWebView ignores the download attribute, and a
// plain link to the API would also go without the bearer token. So inside the
// native app the PDF is written to the app's cache directory through
// @capacitor/filesystem and handed to the share sheet (@capacitor/share), which
// is where an iPhone user saves to Files, prints, or AirDrops it.
//
// The plugins are imported lazily, only on the native branch, so the web
// bundle does not carry them.

import axios from 'axios';
import { buildApiUrl } from '../config/api';

export const pdfUrl = (programId) => buildApiUrl(`/programs/${programId}/pdf`);

/**
 * The filename from a Content-Disposition header, or the fallback.
 *
 * The server already restricts the name to [A-Za-z0-9._-]; this re-applies the
 * rule because the name becomes a path in the cache directory on iOS, and a
 * client should not rely on one server for what reaches its filesystem.
 */
export function filenameFromDisposition(header, fallback = 'program.pdf') {
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header || '');
  let name = match ? match[1] : '';
  try {
    name = decodeURIComponent(name);
  } catch {
    // Not percent-encoded; keep it as sent.
  }
  name = name.replace(/[^A-Za-z0-9._-]+/g, '-').replace(/^[.\-_]+/, '');
  if (!name) return fallback;
  return /\.pdf$/i.test(name) ? name : `${name}.pdf`;
}

/**
 * The message to show for a failed download.
 *
 * With responseType 'blob', axios hands back an error body as a Blob too, so
 * the server's sentence ("This program has no generated or registered block…")
 * has to be read out of it. Without this every failure reads as the generic one,
 * and "nothing to export yet" looks like a broken button.
 */
export async function pdfErrorMessage(error) {
  const data = error?.response?.data;
  if (typeof Blob !== 'undefined' && data instanceof Blob) {
    try {
      const parsed = JSON.parse(await blobText(data));
      if (parsed?.error) return parsed.error;
    } catch {
      // Not JSON; fall through.
    }
  } else if (data?.error) {
    return data.error;
  }
  if (error?.response?.status === 404) return 'There is no PDF for this program yet.';
  return 'The PDF could not be downloaded. Try again shortly.';
}

// Blob.text() where there is one; FileReader where there is not (older
// WebKit, and jsdom).
function blobText(blob) {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsText(blob);
  });
}

/** GET the PDF as a Blob, with the name the server suggested. */
export async function fetchProgramPdf(programId, token, http = axios) {
  const response = await http.get(pdfUrl(programId), {
    headers: { Authorization: `Bearer ${token}` },
    responseType: 'blob',
  });
  const disposition = response.headers?.['content-disposition'];
  return {
    blob: response.data,
    filename: filenameFromDisposition(disposition, `program-${programId}.pdf`),
  };
}

/** Save through a temporary <a download>, the browser's own download path. */
export function saveOnWeb(blob, filename, { doc = document, urls = URL, later = setTimeout } = {}) {
  const href = urls.createObjectURL(blob);
  const link = doc.createElement('a');
  link.href = href;
  link.download = filename;
  link.rel = 'noopener';
  doc.body.appendChild(link);
  link.click();
  link.remove();
  // Revoked after the click has been handled, not during it: Safari starts the
  // download asynchronously and a URL revoked at once downloads nothing.
  later(() => urls.revokeObjectURL(href), 1000);
}

/** A Blob as base64 without the data: prefix, which is what writeFile takes. */
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error);
    reader.onload = () => {
      const result = String(reader.result || '');
      resolve(result.slice(result.indexOf(',') + 1));
    };
    reader.readAsDataURL(blob);
  });
}

/**
 * Whether a share-sheet rejection is the user closing it.
 *
 * Dismissing the sheet rejects the promise ("Share canceled"). That is the
 * user's choice, not a failure, and an error banner for it would be wrong.
 */
export function isShareCancel(error) {
  return /cancel/i.test(String(error?.message || error || ''));
}

/**
 * Save inside the native shell: write to Directory.Cache, then share.
 *
 * Cache, not Documents: the file is a hand-off to the share sheet, and the OS
 * may reclaim it. Keeping a copy in Documents would grow the app's storage with
 * every download and show up in the Files app as clutter nobody asked for.
 */
export async function saveOnNative(blob, filename, plugins) {
  const { Filesystem, Directory, Share } = plugins || (await loadNativePlugins());
  const data = await blobToBase64(blob);
  const { uri } = await Filesystem.writeFile({
    path: filename,
    data,
    directory: Directory.Cache,
  });
  try {
    await Share.share({ title: filename, url: uri, dialogTitle: 'Save or share the program' });
  } catch (error) {
    if (!isShareCancel(error)) throw error;
  }
}

async function loadNativePlugins() {
  const [{ Filesystem, Directory }, { Share }] = await Promise.all([
    import('@capacitor/filesystem'),
    import('@capacitor/share'),
  ]);
  return { Filesystem, Directory, Share };
}
